import {persistReducer} from 'redux-persist';
import produce from 'immer';

import types, {IDialogProps, layoutReducerKey} from './types';
import {Partners} from 'types/partners';
import {Transactions} from 'types/transactions';
import {INav} from './helpers/FoldersHelper';
import {IHolidayCalendar} from 'types/HolidayCalendar';
import {mutateVfsNode, removeLatest, swapArrayElements, updateInArray} from 'common/utils';

import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {IAuthenticationPolicies} from 'types/AuthenticationPolicies';
import {IPasswordData} from 'types/PasswordData';
import {Action, handleAsyncActions} from 'redux/helpers';
import {DiplomatFolder, IDiplomatFolder} from 'types/DiplomatFolder';
import {IKeysResponse} from 'types/KeysResponse';
import {IStatesResponse} from 'types/StatesResponse';
import {sftpUsersTypes} from 'redux/sftp-user';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {IAllGroups} from 'types/diplomat/sftp/AllGroups';
import {castVfsNodes} from 'common/yup/castVfsNode';
import {baseNameComparator} from 'common/stringUtils';
import {cloneDeep, forOwn, merge, times} from 'lodash';
import storage from 'redux-persist/lib/storage';

export const initialLayoutState = {
   isFetching: false,
   error: null,
   keys: {} as IKeysResponse,
   partners: {} as Partners,
   transactions: [] as Transactions,
   vfsGroups: {} as IAllGroups,
   folders: [] as IDiplomatFolder[],
   statesSince: undefined as number | undefined,
   states: {} as IStatesResponse,
   treeFilter: {
      search: '' as string,
   } as {search?: string; excludeEmpty?: boolean},
   treeNav: [] as INav[],
   treeState: {} as any,
   dialogs: [] as IDialogProps[],
   activeDialogStatus: {
      closeButtonLabel: 'Close',
      textLines: [''],
   },

   numJobHistories: {} as any,

   inboundEmailServerTestResult: {} as any,
   calendars: [] as IHolidayCalendar[],

   admins: [] as IDiplomatAdminUser[],
   authPolicies: {} as IAuthenticationPolicies,
   passwordData: {} as IPasswordData,

   audit: {
      archiveUsed: false,
      archiveDateRange: {
         oldestArchiveDate: '',
         youngestArchiveDate: '',
      },
   },

   supportTicketGenerating: false,
   toggledPanels: {
      'File Information': true,
      'Source Partner Profile': true,
      'Destination Partners': true,
      'File Handling': true,
      'Job Execution': true,
      Notifications: true,
      'Pre-Job Processes': true,
      'Post-Job Processes': true,
      Archiving: true,
   } as {[panelName: string]: boolean},

   jobMonitorColumnsOrder: times(10),
} as const;

const layoutReducer = (_state = initialLayoutState, action: Action) => {
   const state = handleAsyncActions(_state, action, types);

   switch (action.type) {
      case types.LOAD_TREE_NAV.SUCCESS:
         return {
            ...state,
            ...action.payload,
            isFetching: false,
            error: null,
         };
      case types.TREE_SEARCH:
         return {
            ...state,
            treeFilter: {
               ...state.treeFilter,
               ...action.payload,
            },
         };
      case sftpUsersTypes.EXPAND_VFS_NODE.REQUEST:
         return {
            ...state,
            treeNav: mutateVfsNode(state.treeNav, action.payload.id, (target) => {
               target.child = target.child || [];
               target.child.unshift(
                  DiplomatFolder.cast({
                     parentId: action.payload.id,
                     objectType: DiplomatObjectType.enum.LOADER,
                  })
               );
            }),
         };
      case sftpUsersTypes.EXPAND_VFS_NODE.SUCCESS: {
         const nodes = action.payload.response;
         const parentPath = action.payload.request.vfspath;
         const _vfsNodes = castVfsNodes(nodes, parentPath);
         const vfsNodes = _vfsNodes.sort(baseNameComparator);

         console.debug('EXPAND_VFS_NODE.SUCCESS', vfsNodes, action.payload, parentPath);
         return {
            ...state,
            treeNav: mutateVfsNode(state.treeNav, parentPath, (target) => {
               target.child = vfsNodes;
            }),
         };
      }
      case sftpUsersTypes.HIDE_LOADER:
         return {
            ...state,
            treeNav: mutateVfsNode(state.treeNav, action.payload.id, (target) => {
               target.child = target.child || [];
               const i = target.child.findIndex(
                  (c) => c.objectType === DiplomatObjectType.enum.LOADER
               );
               target.child.splice(i, 1);
            }),
         };

      case types.UPDATE_STATES_FULL.REQUEST:
         return {
            ...state,
            statesSince: new Date().getTime(),
         };
      case types.UPDATE_STATES_FULL.SUCCESS:
         return {
            ...state,
            states: action.payload,
            isFetching: false,
            error: null,
         };
      case types.UPDATE_STATES_FULL.ERROR:
         return {
            ...state,
            statesSince: undefined,
         };

      case types.GET_STATES.REQUEST:
         return {
            ...state,
            statesSince: new Date().getTime(),
         };
      case types.GET_STATES.SUCCESS: {
         const _states = cloneDeep(state.states);
         const newStates = merge(_states, action.payload);
         return {
            ...state,
            states: newStates,
            isFetching: false,
            error: null,
         };
      }
      case types.GET_STATES.ERROR:
         return {
            ...state,
            statesSince: undefined,
         };

      case types.UPDATE_TREE_STATE:
         return produce(state, (draft) => {
            draft.treeState[action.payload.id] = action.payload;
         });
      case types.GET_NUM_JOB_HISTORIES.SUCCESS:
         return {
            ...state,
            numJobHistories: action.payload,
            error: null,
         };
      case types.TEST_INBOUND_EMAIL_SERVER.SUCCESS:
         return {
            ...state,
            inboundEmailServerTestResult: action.payload,
            error: null,
         };
      case types.LOAD_CALENDARS.SUCCESS:
         return {
            ...state,
            calendars: action.payload.calendars,
         };
      case types.ADD_CALENDAR.SUCCESS:
         return {
            ...state,
            calendars: [...state.calendars, action.payload],
         };
      case types.UPDATE_CALENDAR.SUCCESS:
         return {
            ...state,
            calendars: updateInArray(state.calendars, action.payload, 'id'),
         };
      case types.DELETE_CALENDAR.SUCCESS:
         return {
            ...state,
            calendars: state.calendars.filter((c) => c.id !== action.payload),
         };
      case types.LOAD_USER_ADMINS.SUCCESS:
         return {
            ...state,
            isFetching: false,
            error: null,
            admins: action.payload,
         };
      // case types.ADD_USER_ADMIN.SUCCESS:
      //    return {
      //       ...state,
      //       admins: [...state.admins, action.payload], // payload is empty and causes problems
      //    };
      // case types.DELETE_USER_ADMIN.SUCCESS:
      //    return {
      //       ...state,
      //       admins: removeLatest(state.admins, (admin) => admin.id === action.payload), // payload is empty and causes problems
      //    };
      case types.UPDATE_USER_ADMIN.SUCCESS:
         return {
            ...state,
            admins: updateInArray(state.admins, action.payload, 'id'),
         };

      case types.GET_AUTH_POLICIES.SUCCESS:
      case types.POST_AUTH_POLICIES.SUCCESS:
         return {
            ...state,
            authPolicies: action.payload,
         };
      case types.GET_PASSWORD_DATA.SUCCESS:
      case types.POST_PASSWORD_DATA.SUCCESS:
         return {
            ...state,
            passwordData: action.payload,
         };

      case types.GET_ARCHIVE_USED.SUCCESS:
         return {
            ...state,
            audit: {
               ...state.audit,
               archiveUsed: action.payload,
            },
         };
      case types.GET_ARCHIVE_DATE_RANGE.SUCCESS:
         return {
            ...state,
            audit: {
               ...state.audit,
               archiveDateRange: action.payload,
            },
         };

      case types.SET_ACTIVE_DIALOG_STATUS:
         return {
            ...state,
            activeDialogStatus: produce(state.activeDialogStatus, () => action.payload),
         };
      case types.CLEAR_ACTIVE_DIALOG_STATUS:
         return {
            ...state,
            activeDialogStatus: {},
         };

      case types.OPEN_SUB_DIALOG:
         return {
            ...state,
            dialogs: [...state.dialogs, action.payload],
         };
      case types.CLOSE_SUB_DIALOG:
         return {
            ...state,
            dialogs: removeLatest(
               state.dialogs,
               (d: IDialogProps) => d.id === action.payload.dialogType
            ),
         };

      case types.SEND_SUPPORT_TICKET.REQUEST:
         return {
            ...state,
            supportTicketGenerating: true,
         };
      case types.SEND_SUPPORT_TICKET.ERROR:
      case types.SEND_SUPPORT_TICKET.SUCCESS:
         return {
            ...state,
            supportTicketGenerating: false,
         };

      case types.TOGGLE_PANEL:
         return {
            ...state,
            toggledPanels: {
               ...state.toggledPanels,
               [action.payload]: !state.toggledPanels[action.payload],
            },
         };
      case types.TOGGLE_ALL_PANELS: {
         const toggledPanels = {...state.toggledPanels};
         forOwn(toggledPanels, (p, k) => {
            toggledPanels[k] = action.payload;
         });
         return {
            ...state,
            toggledPanels,
         };
      }

      case types.CHANGE_JOB_MONITOR_COLUMNS_ORDER: {
         const arr = state.jobMonitorColumnsOrder ? [...state.jobMonitorColumnsOrder] : [];
         const newArr = swapArrayElements(arr, action.payload.oldIndex, action.payload.targetIndex);
         // arr.splice(
         //    action.payload.targetIndex,
         //    0,
         //    arr[action.payload.oldIndex] || action.payload.oldIndex
         // );
         // arr.splice(action.payload.oldIndex, 1);

         return {
            ...state,
            jobMonitorColumnsOrder: newArr,
         };
      }

      default:
         return state;
   }
};

let reducer = layoutReducer;
// if (process.env.REACT_APP_PERSISTED === 'true') {
const config = {
   key: layoutReducerKey,
   storage: storage,
   // blacklist: ['error', 'isFetching', 'dialogs'],
   // whitelist: ['toggledPanels'],
   whitelist: ['toggledPanels', 'jobMonitorColumnsOrder'],
};
reducer = persistReducer(config, layoutReducer) as any;
// }
export default reducer;
